import { Survey } from '@/api/surveys';
import { Badge } from '@/components/ui/badge';
import { toTitleCase } from '@/utils/toTitleCase';

interface Props {
    survey: Survey;
    answers: Record<string, unknown>;
}

export default function SurveyResponseViewer({ survey, answers }: Props) {
    const optionLabel = (
        field: Survey['sections'][number]['questions'][number],
        value: unknown,
    ) => {
        const option = field.options?.find((item) => {
            const optionValue = typeof item === 'string' ? item : item.value;
            return optionValue === value;
        });

        if (!option) return String(value);
        return typeof option === 'string' ? option : option.label;
    };

    const renderValue = (
        value: unknown,
        type: string,
        field: Survey['sections'][number]['questions'][number],
    ) => {
        if (value === undefined || value === null || value === '') {
            return <span className="text-muted-foreground">-</span>;
        }

        if (Array.isArray(value)) {
            if (value.length === 0) return <span className="text-muted-foreground">-</span>;
            return (
                <div className="flex flex-wrap gap-2">
                    {value.map((item, idx) => (
                        <Badge key={idx} variant="secondary" className="px-3 py-1 text-sm">
                            {optionLabel(field, item)}
                        </Badge>
                    ))}
                </div>
            );
        }

        if (type === 'select') {
            if (value === 'other') {
                return (
                    <span>{String(answers?.[`${field.field_name}_other_answer`] || 'Outro')}</span>
                );
            }

            return <span>{optionLabel(field, value)}</span>;
        }

        if (type === 'checkbox' || typeof value === 'boolean') {
            return value ? 'Sim' : 'Não';
        }

        return <span>{String(value)}</span>;
    };

    return (
        <div className="space-y-12">
            {survey.sections.map((section) => (
                <div
                    key={section.id}
                    id={`survey-section-${section.id}`}
                    className="scroll-mt-28 space-y-6 rounded-xl border border-border bg-card p-6 shadow-sm"
                >
                    <div className="mb-4 border-b border-border pb-2">
                        <h2 className="text-xl font-semibold text-foreground">{section.title}</h2>
                        {section.description && (
                            <p className="text-muted-foreground">{section.description}</p>
                        )}
                    </div>

                    <div className="space-y-8">
                        {section.questions.map((field, index) => {
                            const questionLabel = field.field_label
                                ? field.field_label
                                : toTitleCase(field.field_name.replace(/_/g, ' '));
                            const answer = answers?.[field.field_name];
                            const type = field.field_type?.toLowerCase() || '';

                            return (
                                <div
                                    key={index}
                                    className="border-b border-border pb-6 last:border-0 last:pb-0"
                                >
                                    <h3 className="mb-2 text-sm font-medium uppercase tracking-wide text-muted-foreground">
                                        {questionLabel}
                                    </h3>
                                    <div className="text-lg font-semibold leading-relaxed text-foreground">
                                        {renderValue(answer, type, field)}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}
        </div>
    );
}
